import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import api from '../../services/api';

const DEFAULT_SAMPLE_TEXT = "Hello, thanks for seeing me today. I've been finding things quite difficult lately.";

const VoiceSelector = ({
  value,
  onChange,
  provider = 'elevenlabs',
  sampleText,
  label = 'Patient Voice',
  showPreview = true,
}) => {
  const [voices, setVoices] = useState([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchVoices = async () => {
      setIsLoadingVoices(true);
      setError(null);
      try {
        // Server-backed voices list for ALL providers (cached, authenticated).
        // The direct api.elevenlabs.io call required the API key in the web
        // bundle (key-removal plan, Phase 1.2). Map the server's standardized
        // shape back to the legacy ElevenLabs shape this component renders.
        const token = localStorage.getItem('token');
        const response = await api.get(`/tts/voices?provider=${provider}`, token);
        const body = response.data || response;
        const standardized = body.voices || body.data?.voices || [];
        setVoices(standardized.map(v => ({
          voice_id: v.providerVoiceId || v.id || v.voice_id,
          name: v.name,
          labels: v.metadata?.labels || v.labels || {
            gender: v.gender,
            accent: v.accent,
            age: v.ageGroup,
            use_case: v.useCase,
          },
          preview_url: v.previewUrl || v.preview_url,
        })));
      } catch (err) {
        console.error('Failed to fetch voices:', err);
        setError('Failed to load voices');
      } finally {
        setIsLoadingVoices(false);
      }
    };
    fetchVoices();
  }, [provider]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePreview = async () => {
    if (!value) return;

    // If already playing, stop
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }

    setIsPlaying(true);
    try {
      // Preview via the server (POST /api/tts/test) — no provider key in the
      // browser (key-removal plan, Phase 1.2). Returns base64 mp3.
      const text = (sampleText || DEFAULT_SAMPLE_TEXT).substring(0, 500);
      const token = localStorage.getItem('token');
      const response = await api.post('/tts/test', {
        text,
        voiceId: value,
        provider,
      }, token);
      const body = response.data || response;
      if (!body?.audio) throw new Error(body?.msg || 'No audio returned');

      const byteString = atob(body.audio);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      await audio.play();
    } catch (err) {
      console.error('Voice preview error:', err);
      setIsPlaying(false);
    }
  };

  const formatVoiceLabel = (voice) => {
    const labels = voice.labels || {};
    const parts = [voice.name];
    const meta = [labels.gender, labels.accent, labels.age, labels.useCase]
      .filter(Boolean)
      .join(', ');
    if (meta) {
      parts.push(`(${meta})`);
    }
    return parts.join(' ');
  };

  if (isLoadingVoices) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading voices...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <FormControl fullWidth size="small" error={!!error}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || '')}
          label={label}
        >
          <MenuItem value="">
            <em>None (use platform default)</em>
          </MenuItem>
          {voices.map((voice) => (
            <MenuItem key={voice.voice_id} value={voice.voice_id}>
              {formatVoiceLabel(voice)}
            </MenuItem>
          ))}
        </Select>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </FormControl>
      {showPreview && (
        <Button
          variant="outlined"
          size="small"
          disabled={!value || (isLoadingVoices)}
          onClick={handlePreview}
          startIcon={isPlaying ? <StopIcon /> : <PlayArrowIcon />}
          sx={{ minWidth: 110, mt: '1px', height: 40 }}
        >
          {isPlaying ? 'Stop' : 'Preview'}
        </Button>
      )}
    </Box>
  );
};

export default VoiceSelector;
